"use client";
import { capRedemption } from "@/lib/loyalty-cap";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Badge, Button, Card, Radio, Row, StickyBar, Toggle, TopBar } from "@/components/ui";
import { MarketingConsentToggle } from "@/components/MarketingConsentToggle";
import { consentApi } from "@/api/services";
import { AuthFlow } from "@/components/AuthFlow";
import { useCart } from "@/store/cart";
import { useAuth } from "@/store/auth";
import { useBranch } from "@/store/branch";
import { ChangeForField } from "@/components/checkout/ChangeForField";
import { changeForEnabled, changeForError, changeForPayload } from "@/lib/changeFor";
import { useTable } from "@/store/table";
import { useGroupCart } from "@/store/group-cart";
import { useFulfillment } from "@/store/fulfillment";
import { useAddresses } from "@/hooks/useAddresses";
import { usePushToken } from "@/hooks/usePushToken";
import { PushOptIn } from "@/components/PushOptIn";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";
import { useBranchEta, useMinOrder, useSyncNearestBranch } from "@/hooks/useBranchInfo";
import { useBranchOpen, isBranchOpenAt } from "@/hooks/useBranchOpen";
import { useSavedCards } from "@/hooks/useSavedCards";
import { chargeableSavedCards } from "@/lib/saved-cards";
import { useMenu } from "@/hooks/useMenu";
import { useRedemption } from "@/hooks/useRedemption";
import { useEarningsEstimate } from "@/hooks/useEarningsEstimate";
import { priceQuoteMode } from "@/lib/feature-flags";
import type { SurchargeDefinition, TaxRef } from "@/lib/surcharges";
import { loyaltyApi } from "@/api/services";
import {
  promotionLoyaltySignals,
  orderApi,
  paymentApi,
  surchargesApi,
  type PaymentIntent,
  type PaymentIntentCreatePayload,
} from "@/api/services";
import { track } from "@/lib/analytics";
import { getAttributionForOrder, hasAttribution } from "@/lib/attribution";
import { getReferralCode, clearReferralCode } from "@/lib/referral-capture";
import { launchPaymobPixel, cleanupPaymob } from "@/lib/paymob";
import { launchFawryCheckout, cleanupFawry } from "@/lib/fawrypay";
import {
  mountStripeCardElement,
  confirmStripeCardPayment,
  confirmStripeSavedCardPayment,
  type StripeMount,
} from "@/lib/stripe";
import { branchAddressLine, branchDisplayName } from "@/lib/fulfillment";
import { savePendingPaymentReturn } from "@/lib/pendingPayment";
import { cartLineToOrderLineInput, grantedLineToOrderLineInput } from "@/lib/order-line-payload";
import { usePromotions } from "@/hooks/usePromotions";
import { useTillPricing } from "@/hooks/useTillPricing";
import { ApiError } from "@/api/http";
import {
  ChannelEnum,
  OrderTypeEnum,
  PreferPaymentMethodEnum,
  type AddressInput,
  type AppSchemasCustomerAddress,
  type OrderInput,
  type OrderOutput,
  type OrderLineInput,
  type PaymentIntentResponse,
  type PaymentMethodOption,
  type PaymentPayloadInput,
  type ScheduledSlot,
} from "@/api/online-ordering-service/model";
import { SignInModal } from "@/components/SignInModal";
import { CheckoutCrossSell } from "@/components/offers/CheckoutCrossSell";
import { RecommendationStrip } from "@/components/RecommendationStrip";
import { StorefrontNote } from "@/components/StorefrontNote";
import { useQuoteEta } from "@/hooks/useQuoteEta";
import { quoteLabel } from "@/lib/promise";
import { legacyMinLeadMinutes, resolveMinLeadMinutes } from "@/lib/orderEta";
import type { QuoteEtaRequest, PriceQuoteRequest } from "@/api/online-ordering-service/model";
import { quoteApi, priceQuoteApi, isPriceChanged, priceChangedQuote, isLoyaltyRedemptionInvalid } from "@/api/services";
import { usePriceQuote } from "@/hooks/usePriceQuote";
import { GrantedLineRow } from "@/components/checkout/GrantedLineRow";
import type { PriceQuote, PriceQuoteLine } from "@/api/online-ordering-service/model";
import { SlotPicker } from "@/components/SlotPicker";
import {
  asapPausedNextSlot,
  bookableSlots,
  fetchSlots,
  isSlotFullError,
  slotLeadFloor,
  slotListIsStale,
  slotMeetsLead,
} from "@/lib/slots";

const cardHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 14px",
  borderBottom: "1px solid var(--border)",
  fontWeight: 700,
  fontSize: 14,
  color: "var(--ink-1)",
};
const optionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  background: "transparent",
  border: 0,
  width: "100%",
  textAlign: "start",
  cursor: "pointer",
  fontFamily: "inherit",
};
const editBtn: React.CSSProperties = {
  marginInlineStart: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "5px 10px",
  borderRadius: 999,
  border: "1px solid var(--border)",
  background: "var(--surface-1)",
  color: "var(--ink-2)",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const PAYMENT_SHEET_ID = "paymob-payment-container";
const STRIPE_ELEMENT_ID = "stripe-card-element";

function shortLine(a: AppSchemasCustomerAddress): string {
  return [a.street, a.street2].filter(Boolean).join(", ") || a.city;
}

function toAddressInput(a: AppSchemasCustomerAddress): AddressInput {
  const city =
    (a.city && a.city.trim()) ||
    (a.state && a.state.trim()) ||
    (a.street && a.street.trim()) ||
    "City";
  return {
    name: a.name ?? null,
    street: a.street,
    street2: a.street2 ?? null,
    city,
    state: a.state ?? null,
    postalCode: a.postalCode ?? null,
    country: a.country,
    coordinates: a.coordinates ?? null,
  };
}

export function getDisplayName(name: { [k: string]: unknown } | undefined | null, lang: string): string {
  if (!name) return "";
  if (typeof name === "string") return name;
  const normalizedLang = lang.toLowerCase().split(/[-_]/)[0];
  // Pick only non-empty strings. Locale maps often carry an empty value for a
  // language the merchant left blank (e.g. {en: "Paymob Egypt", ar: ""}); `??`
  // would stop on that "" and we'd render nothing (and callers fall back to the
  // raw id/UUID). Treat blank/whitespace-only as missing and keep searching.
  const pick = (v: unknown): string | undefined => {
    const s = typeof v === "string" ? v.trim() : "";
    return s ? (v as string) : undefined;
  };
  return (
    pick(name[lang]) ??
    pick(name[normalizedLang]) ??
    pick(name.en) ??
    pick(name.ar) ??
    pick(Object.values(name).find((v) => pick(v))) ??
    ""
  );
}

type ProviderFamily = "paymob" | "fawry" | "stripe" | "cash" | "unknown";
type CheckoutBlocker = {
  kind:
    | "cart"
    | "signin"
    | "branch"
    | "closed"
    | "address"
    | "branch_loading"
    | "payment"
    | "time";
  message: string;
};

function classifyMethod(method: PaymentMethodOption): ProviderFamily {
  const provider = ((method as { provider?: string | null }).provider ?? "").toLowerCase();
  const type = (method.paymentMethodType ?? "").toLowerCase();
  const id = method.id.toLowerCase();
  const display = getDisplayName(method.displayName, "en").toLowerCase();
  if (type === "cash" || id.includes("cash") || id.includes("cod")) return "cash";
  if (provider.includes("paymob") || id.includes("paymob") || type.includes("paymob") || display.includes("paymob")) return "paymob";
  if (provider.includes("fawry") || id.includes("fawry") || type.includes("fawry") || display.includes("fawry")) return "fawry";
  if (provider.includes("stripe") || id.includes("stripe") || type.includes("stripe") || display.includes("stripe")) return "stripe";
  if (type === "online_payment") return "unknown";
  return "unknown";
}

function classifyProvider(intent: PaymentIntent): ProviderFamily {
  const key = (intent.providerKey ?? "").toLowerCase();
  if (key.includes("paymob")) return "paymob";
  if (key.includes("fawry")) return "fawry";
  if (key.includes("stripe")) return "stripe";
  return "unknown";
}

function intentToProviderInput(intent: PaymentIntent): PaymentIntentResponse {
  return {
    status: intent.status ?? "requires_action",
    clientSecret: intent.clientSecret ?? null,
    transactionRef: intentRef(intent) || null,
    amount: intent.amount ?? (intent.metadata?.amount as number | undefined) ?? 0,
    currency: intent.currency ?? (intent.metadata?.currency as string | undefined) ?? "",
    failureReason: null,
    metadata: (intent.metadata as { [key: string]: unknown } | null | undefined) ?? null,
    providerKey: intent.providerKey ?? null,
    publicKey: intent.publicKey ?? null,
  } as PaymentIntentResponse;
}

function intentRef(intent: PaymentIntent): string {
  return (
    intent.transactionRef ??
    (intent.metadata?.transactionRef as string | undefined) ??
    (intent.metadata?.merchantRefNum as string | undefined) ??
    intent.id ??
    intent.clientSecret ??
    ""
  );
}

function isIntentSuccessful(intent: PaymentIntent): boolean {
  const status = (intent.status ?? "").toLowerCase();
  return status === "successful" || status === "succeeded" || status === "paid";
}

function isActionRequired(intent: PaymentIntent): boolean {
  const status = (intent.status ?? "").toLowerCase();
  return status === "requires_action" || status === "requires_confirmation";
}

function makeOrderId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function injectPrice(text: string, amount: number) {
  const parts = text.split("___PRICE___");
  if (parts.length === 1) return text;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 3 }}>
      {parts[0]}
      <Price amount={amount} />
      {parts[1]}
    </span>
  );
}

function toDateTimeLocalString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const hh = pad(date.getHours());
  const mm = pad(date.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

/**
 * Entered codes for the quote/order body: `promoCode` is the first (what older
 * services read), `promoCodes` only when there are several, so a single-code
 * quote hashes exactly as before.
 */
export function codesPayload(raw: ReadonlyArray<string | null | undefined>): {
  promoCode: string | null;
  promoCodes?: string[];
} {
  const codes = raw.map((c) => (c ?? "").trim()).filter((c) => c);
  return codes.length > 1 ? { promoCode: codes[0], promoCodes: codes } : { promoCode: codes[0] ?? null };
}

export function CheckoutScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    cart,
    clearCart,
    removeLine,
    orderNote,
    appliedPromotion,
    appliedPromotions,
    appendOrderId,
    setAppendOrderId,
  } = useCart();
  const { isLoggedIn, user } = useAuth();
  const { branchId, branchConfig, branch, branchSlug, requiresBranchSelection } = useBranch();
  const { isDineIn, tableInfo } = useTable();
  const { mode, selectedAddressId, setSelectedAddressId, manualAddress } = useFulfillment();
  const groupCart = useGroupCart();
  // const { mode, selectedAddressId, setSelectedAddressId } = useFulfillment();
  const { addresses, loading: addressesLoading } = useAddresses();
  const { format, currency } = useFormatPrice();
  const { etaMin, etaMax } = useBranchEta();
  const { isClosed, opensAt } = useBranchOpen();
  // VITE_PRICE_QUOTE_MODE: "enforce" = server-priced checkout (quote + quoteId);
  // "shadow" (default) = local pricing, order created without quoteId.
  const enforceQuote = priceQuoteMode() === "enforce";
  const minOrder = useMinOrder();
  useSyncNearestBranch();

  // Surcharges (Task 5) — active tenant definitions from the BFF, matched
  // client-side against branch/order-type/source/schedule at compute time
  // (see `computeAppliedSurcharges`). The BFF resolves each surcharge's
  // `taxIds` entries to the tenant's tax records via `taxesById` alongside
  // `data`; a surcharge referencing a `taxIds` entry we still can't resolve
  // (e.g. an older BFF deployment that predates this field) is simply left
  // untaxed client-side (the port already no-ops on an unresolved tax id).
  // NOTE: client-computed totals persist as-is — order-service's surcharge
  // validation is log-only in v1 and does NOT correct pricing, so an
  // unresolved tax here means the surcharge tax is permanently absent from
  // the order. Keep the BFF's taxesById resolution healthy.
  const [surchargeDefs, setSurchargeDefs] = useState<SurchargeDefinition[]>([]);
  const [surchargeTaxesById, setSurchargeTaxesById] = useState<Record<string, TaxRef>>({});
  useEffect(() => {
    let cancelled = false;
    surchargesApi
      .list(branchId ?? undefined)
      .then(({ definitions, taxesById }) => {
        if (!cancelled) {
          setSurchargeDefs(definitions);
          setSurchargeTaxesById(taxesById);
        }
      })
      .catch((err) => {
        console.warn("[Checkout] surcharges fetch failed (non-fatal)", err);
      });
    return () => {
      cancelled = true;
    };
  }, [branchId]);
  const { cards: allSavedCards } = useSavedCards();
  // Only offer cards the provider can actually charge — see chargeableSavedCards.
  const savedCards = useMemo(
    () => chargeableSavedCards(allSavedCards),
    [allSavedCards],
  );
  const { items: menuItems, menuId, menuTaxExempt } = useMenu();
  const { promotions } = usePromotions();
  // Geofenced promotions need the delivery location (fail closed without it).
  const pricingCoords = useMemo(() => {
    if (isDineIn || mode !== "delivery") return null;
    const a = (selectedAddressId ? addresses.find((x) => x.id === selectedAddressId) : null) ?? manualAddress;
    const c = a?.coordinates;
    return c && typeof c.latitude === "number" && typeof c.longitude === "number"
      ? { lat: c.latitude, lng: c.longitude }
      : null;
  }, [isDineIn, mode, selectedAddressId, addresses, manualAddress]);
  // Shadow-mode checkout prices exactly as the POS till (owner ruling
  // 2026-10-10). Pre-loyalty pass: redemption eligibility is derived from it.
  const tillPre = useTillPricing({
    menuItems, menuTaxExempt, promotions, surcharges: surchargeDefs, surchargeTaxesById,
    deliveryCoordinates: pricingCoords,
  });

  const allowedMethods: PaymentMethodOption[] = useMemo(
    () => branchConfig?.allowedPaymentMethods ?? [],
    [branchConfig],
  );

  const [pay, setPay] = useState<string>("");
  const [savedCardId, setSavedCardId] = useState<string | null>(null);
  // Save-card opt-in for Fawry checkout. Fawry's Express Checkout
  // accepts ``saveCardInfo: true`` to bind the new token to the
  // customerProfileId; we then retrieve it server-side after PAID.
  const [saveCardOptIn, setSaveCardOptIn] = useState<boolean>(false);
  // Default tip 0 — never opt the user in by default. Tipping is hidden for dine-in.
  const [tip, setTip] = useState(0);
  const [contactless, setContactless] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  // Marketing consent must be an ACTIVE opt-in: default OFF (never pre-checked).
  // A pre-ticked box is not valid consent under GDPR/most regimes.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  // Consent-gated web-push: when the user opts in we request browser
  // permission and register an FCM token into customer_devices.
  const { enablePush, disablePush } = usePushToken();
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const placingRef = useRef(false);

  // Toggling marketing consent ON also acquires a web-push token (browser
  // permission prompt → customer_devices registration); toggling OFF unregisters
  // it. All push side-effects are best-effort and never block the toggle.
  function handleMarketingConsentChange(next: boolean) {
    setMarketingOptIn(next);
    // Best-effort; enablePush() guards on pushSupported() internally and no-ops
    // when web-push isn't available or permission is denied.
    if (next) {
      void enablePush();
    } else {
      void disablePush();
    }
  }
  const tipsEnabled = !isDineIn;

  const [timeMode, setTimeMode] = useState<"asap" | "scheduled">("asap");
  const [scheduledTime, setScheduledTime] = useState("");
  // Promise Time Phase 2: when the quote returns bookable slots (branches with a slot
  // budget that show their promise), "Schedule" lists them instead of the free
  // datetime input. An empty list or a failed quote keeps the legacy picker as is.
  const [selectedSlot, setSelectedSlot] = useState<ScheduledSlot | null>(null);
  const [slots, setSlots] = useState<ScheduledSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [slotsRefresh, setSlotsRefresh] = useState(0);
  // When the current list was fetched (success or failure); older than 5 min means refetch.
  const [slotsFetchedAt, setSlotsFetchedAt] = useState<number | null>(null);
  // Wall clock for the lead floor, ticking while "Schedule" is open.
  const [nowMs, setNowMs] = useState(() => Date.now());
  // The lead floor (minutes) for slots, derived from the quote further down
  // (`resolveMinLeadMinutes`); until the first settle the legacy floor applies.
  const [slotLead, setSlotLead] = useState<number | null>(null);
  const visibleSlots = useMemo(
    () =>
      bookableSlots(
        slots,
        slotLead ?? legacyMinLeadMinutes(branch, { isDineIn, mode }),
        nowMs,
      ),
    [slots, slotLead, branch, isDineIn, mode, nowMs],
  );
  // Slots apply only when some are still bookable; otherwise the legacy picker (with
  // its own lead, 7-day and opening-hours checks) runs as before.
  const useSlots = visibleSlots.length > 0;
  const scheduledIso: string | null =
    timeMode !== "scheduled"
      ? null
      : useSlots
      ? selectedSlot?.start ?? null
      : scheduledTime
      ? new Date(scheduledTime).toISOString()
      : null;

  // Sheet state for online providers.
  const [paymentSheet, setPaymentSheet] = useState<{
    intent: PaymentIntent;
    family: ProviderFamily;
    order: OrderOutput;
  } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [failedPayment, setFailedPayment] = useState<{
    intent: PaymentIntent;
    family: ProviderFamily;
    order: OrderOutput;
  } | null>(null);
  const stripeMountRef = useRef<StripeMount | null>(null);
  const promptedSignInRef = useRef(false);
  const promptedSaveRef = useRef(false);

  useEffect(() => {
    if (isDineIn || mode !== "delivery" || addressesLoading || addresses.length === 0) return;
    if (selectedAddressId || manualAddress) {
      const found = addresses.find((a) => a.id === selectedAddressId);
      if (found || manualAddress) return;
    }
    const primary = addresses.find((a) => a.isPrimary) ?? addresses[0];
    setSelectedAddressId(primary.id);
  }, [
    addresses,
    addressesLoading,
    isDineIn,
    mode,
    selectedAddressId,
    manualAddress,
    setSelectedAddressId,
  ]);

  // If a guest user with a manual address logs in, redirect them to save it.
  useEffect(() => {
    if (isLoggedIn && manualAddress && !promptedSaveRef.current && !addressesLoading) {
      // Check if this manual address already matches an existing saved address by coordinates.
      const match = addresses.find((a) => {
        if (!a.coordinates || !manualAddress.coordinates) return false;
        const latDiff = Math.abs(a.coordinates.latitude - manualAddress.coordinates.latitude);
        const lngDiff = Math.abs(a.coordinates.longitude - manualAddress.coordinates.longitude);
        // ~20m threshold
        return latDiff < 0.0002 && lngDiff < 0.0002;
      });

      if (match) {
        setSelectedAddressId(match.id);
        return;
      }

      promptedSaveRef.current = true;
      navigate("/addresses/new?returnTo=checkout");
    }
  }, [
    isLoggedIn,
    manualAddress,
    addresses,
    addressesLoading,
    navigate,
    setSelectedAddressId,
  ]);

  useEffect(() => {
    if (isDineIn || isLoggedIn || cart.length === 0 || promptedSignInRef.current) return;
    promptedSignInRef.current = true;
    setSignInOpen(true);
  }, [cart.length, isDineIn, isLoggedIn]);

  // Default payment method: first allowed.
  useEffect(() => {
    if (!pay && allowedMethods.length > 0) {
      const preferred =
        allowedMethods.find((m) => classifyMethod(m) === "stripe") ??
        allowedMethods.find((m) => classifyMethod(m) !== "cash") ??
        allowedMethods[0];
      setPay(preferred.id);
    }
  }, [allowedMethods, pay]);

  const selectedMethod = useMemo(
    () => allowedMethods.find((m) => m.id === pay) ?? null,
    [allowedMethods, pay],
  );
  const selectedFamily = selectedMethod ? classifyMethod(selectedMethod) : "unknown";

  // Cart gross (the tip percentage base and the guards below).
  const subtotal = cart.reduce((s, l) => s + l.price * l.qty, 0);
  const prePricing = tillPre.pricing;
  const deliveryFee = prePricing.deliveryFeeAmount;
  const tipAmt = tipsEnabled ? subtotal * (tip / 100) : 0;
  const orderType = tillPre.orderType;
  // Rider P1f: COD "Change for" on delivery orders, gated by the branch's DMS policy flag.
  const [changeForRaw, setChangeForRaw] = useState("");
  const showChangeFor =
    selectedFamily === "cash" &&
    orderType === OrderTypeEnum.Delivery &&
    changeForEnabled(branch as { changeForAtCheckout?: boolean | null } | null);
  const grandBeforeLoyalty = prePricing.totalAmount;

  const selectedAddress = useMemo(
    () => (selectedAddressId ? addresses.find((a) => a.id === selectedAddressId) : null) ?? manualAddress,
    [addresses, selectedAddressId, manualAddress],
  );

  // Promise Time: the server quotes the window and the scheduled lead time.
  // Checkout renders the quote and never computes an ETA. The scheduled-time
  // floor must never fail open while the quote is loading or has failed:
  // `resolveMinLeadMinutes` only lets a successful, settled quote's
  // `minimumLeadMinutes` override `legacyMinLeadMinutes` (the old
  // `kitchen + delivery + 15` rule), so the client floor always exists even
  // though order-service does not enforce the lead on create.
  const deliveryAreaId =
    !isDineIn && mode === "delivery"
      ? (branch as { deliveryAreaId?: string | null } | null)?.deliveryAreaId ?? null
      : null;
  const quoteRequest = useMemo<QuoteEtaRequest | null>(() => {
    if (isDineIn || !branchId || cart.length === 0) return null;
    const coords = mode === "delivery" ? selectedAddress?.coordinates ?? null : null;
    return {
      branchId,
      orderType,
      lines: cart.map((l) => ({ itemId: l.itemId, quantity: l.qty })),
      deliveryAreaId,
      deliveryCoordinates:
        coords && typeof coords.latitude === "number" && typeof coords.longitude === "number"
          ? { lat: coords.latitude, lng: coords.longitude }
          : null,
      scheduledTimestamp: scheduledIso,
    };
  }, [isDineIn, branchId, cart, mode, selectedAddress, orderType, deliveryAreaId, scheduledIso]);
  const { quote, loading: quoteLoading, error: quoteError } = useQuoteEta(quoteRequest);

  // Loyalty redemption — fetch eligible rewards for this draft order, allow
  // the user to opt in via a toggle, and subtract the redeemed value from
  // the final total. Mirrors v1's check-out useRewards/redemptionData flow.
  const [useRewards, setUseRewards] = useState(false);
  // Pre-loyalty, pre-tip totals of the latest price quote (set further down,
  // once the quote hook has run). The server validates a redemption against
  // these same numbers, so the options offered here must come from them too;
  // client math is only a stand-in until the first quote lands.
  const [quotePreLoyalty, setQuotePreLoyalty] = useState<{ itemsSubtotal: number; total: number } | null>(null);
  const redemptionDraft = useMemo(() => {
    if (!isLoggedIn || !user?.id || subtotal <= 0 || !branchId) return null;
    return {
      branchId,
      orderType,
      currency,
      customerId: user.id,
      pricingSummary: {
        currency,
        itemsSubtotal: quotePreLoyalty?.itemsSubtotal ?? prePricing.itemsSubtotal,
        deliveryFeeAmount: deliveryFee,
        taxAmount: prePricing.taxAmount,
        totalAmount: quotePreLoyalty?.total ?? grandBeforeLoyalty,
      },
    };
  }, [
    isLoggedIn,
    user?.id,
    branchId,
    orderType,
    currency,
    subtotal,
    deliveryFee,
    prePricing.itemsSubtotal,
    prePricing.taxAmount,
    grandBeforeLoyalty,
    quotePreLoyalty?.itemsSubtotal,
    quotePreLoyalty?.total,
  ]);
  const redemption = useRedemption({
    orderDraft: redemptionDraft,
    enabled: !!redemptionDraft,
  });
  // What this order earns (earn rules applied server-side, e.g. a first-order
  // welcome bonus). Same draft as redemption: pre-loyalty totals.
  const earnings = useEarningsEstimate({
    orderDraft: redemptionDraft,
    enabled: !!redemptionDraft,
  });
  // Reset the toggle if eligibility goes away (e.g. cart total dropped).
  useEffect(() => {
    if (redemption.disabled && useRewards) setUseRewards(false);
  }, [redemption.disabled, useRewards]);
  // What the customer asked to redeem, in the order-create shape. Points are
  // whole units (Order.loyaltyPointsRedeemed is an int on the server).
  const redemptionInput = useMemo(() => {
    const opt = useRewards ? redemption.option : null;
    if (!opt) return null;
    if (opt.type === "points") {
      const points = Math.floor(opt.maxRedeemable ?? 0);
      return points > 0 ? { loyaltyProgramId: opt.programId ?? null, loyaltyPointsRedeemed: points } : null;
    }
    const cashback = Math.floor((opt.redeemableValue ?? 0) * 100) / 100;
    return cashback > 0 ? { loyaltyProgramId: opt.programId ?? null, loyaltyCashbackRedeemed: cashback } : null;
  }, [useRewards, redemption.option]);
  const requestedTip = Math.round(tipAmt * 100) / 100;

  // Server-priced checkout (spec 2026-09-23 quote-and-lock): the BFF prices
  // the basket, including any auto-applied free-item promotions (granted
  // lines). Totals shown below and the create-order payload come from this
  // quote, never from client math — there is no fallback to client pricing
  // (memory `feedback_no_static_fallback_when_going_dynamic`).
  // Shadow mode (VITE_PRICE_QUOTE_MODE unset/"shadow"): no price quote is
  // requested; checkout prices locally below and the order goes without quoteId.
  const priceQuoteRequest = useMemo<PriceQuoteRequest | null>(() => {
    if (!enforceQuote || !branchId || cart.length === 0) return null;
    return {
      branchId,
      orderType,
      orderLines: cart.map((line, idx) => {
        const input = cartLineToOrderLineInput(line, idx + 1);
        return {
          itemId: input.itemId,
          quantity: input.quantity,
          itemName: input.itemName,
          selectedVariants: input.selectedVariants,
          selectedModifiers: input.selectedModifiers,
          notes: input.notes,
        };
      }),
      customerPhone: user?.phone ?? null,
      deliveryAddress:
        !isDineIn && mode === "delivery" && selectedAddress ? toAddressInput(selectedAddress) : null,
      ...codesPayload(appliedPromotions.map((p) => p.promoCode)),
      scheduledTimestamp: scheduledIso,
      ...(menuId ? { menuId } : {}),
      // Tip and loyalty redemption are priced into the quote, so its total is
      // what the customer pays. Changing either re-quotes (debounced).
      ...(requestedTip > 0 ? { tipAmount: requestedTip } : {}),
      ...(redemptionInput ?? {}),
    };
  }, [
    enforceQuote, branchId, cart, orderType, user?.phone, isDineIn, mode, selectedAddress, appliedPromotions, scheduledIso,
    requestedTip, redemptionInput, menuId,
  ]);
  const {
    quote: priceQuote,
    loading: priceQuoteLoading,
    error: priceQuoteError,
    replace: replacePriceQuote,
  } = usePriceQuote(priceQuoteRequest);
  // Free-item lines: the quote's (enforce) or the till's own (shadow).
  const grantedLines = enforceQuote
    ? priceQuote?.orderLines.filter((l) => !!l.grantedByPromotionId) ?? []
    : prePricing.lines
        .filter((l) => !!l.grantedByPromotionId)
        .map((l, i) =>
          grantedLineToOrderLineInput(l, menuItems.find((m) => m.id === l.itemId), cart.length + i + 1),
        );

  // Manual retry for a failed price quote (the auto-quoting effect only fires
  // when `priceQuoteRequest` changes, so a same-basket retry needs its own path).
  const [priceQuoteRetry, setPriceQuoteRetry] = useState<{ loading: boolean; error: Error | null }>({
    loading: false,
    error: null,
  });
  function retryPriceQuote(): void {
    if (!priceQuoteRequest) return;
    setPriceQuoteRetry({ loading: true, error: null });
    priceQuoteApi
      .quote(priceQuoteRequest)
      .then((q) => {
        replacePriceQuote(q);
        setPriceQuoteRetry({ loading: false, error: null });
      })
      .catch((err: unknown) => {
        setPriceQuoteRetry({
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });
  }
  // The server refused the redemption (balance or cap moved): drop the toggle so
  // the next quote prices the order without it, rather than blocking checkout.
  useEffect(() => {
    if (useRewards && isLoyaltyRedemptionInvalid(priceQuoteError ?? priceQuoteRetry.error)) setUseRewards(false);
  }, [priceQuoteError, priceQuoteRetry.error, useRewards]);
  const priceQuoteBusy = priceQuoteLoading || priceQuoteRetry.loading;
  const priceQuoteFailed = !priceQuote && !!(priceQuoteError || priceQuoteRetry.error);
  const quotedPricing = priceQuote?.pricingSummary;
  const quotedTip = quotedPricing?.tipAmount ?? 0;
  const quotedReward = quotedPricing?.loyaltyDiscountAmount ?? 0;
  useEffect(() => {
    if (!quotedPricing) return;
    const itemsSubtotal = quotedPricing.itemsSubtotal ?? 0;
    const total = Math.round(((quotedPricing.totalAmount ?? 0) + quotedReward - quotedTip) * 100) / 100;
    setQuotePreLoyalty((prev) =>
      prev && prev.itemsSubtotal === itemsSubtotal && prev.total === total ? prev : { itemsSubtotal, total },
    );
  }, [quotedPricing, quotedReward, quotedTip]);

  // Stale-quote recovery (409 PRICE_CHANGED on order creation, D3): the last
  // total shown to the customer before a create attempt was rejected because
  // the server re-priced the basket differently in between.
  const [priceChangedBanner, setPriceChangedBanner] = useState<{ from: number; to: number } | null>(null);
  useEffect(() => {
    setPriceChangedBanner(null);
    setPriceQuoteRetry({ loading: false, error: null });
  }, [priceQuoteRequest]);
  const quoteText = quoteLabel(quote, t, { locale: i18n.language });
  const asapQuoteText = timeMode === "asap" ? quoteText : null;
  const scheduledQuoteText = timeMode === "scheduled" ? quoteText : null;
  const legacyMinLead = legacyMinLeadMinutes(branch, { isDineIn, mode });
  const minLeadMinutes = resolveMinLeadMinutes(quote, quoteLoading, quoteError, legacyMinLead);
  // I1: the slot list must honour the same lead floor. While the quote reloads (every
  // slot pick changes scheduledIso) keep the last settled server lead so the list does
  // not jump to the larger legacy floor and drop the pick.
  const [lastServerLead, setLastServerLead] = useState<number | null>(null);
  useEffect(() => {
    setLastServerLead(null);
  }, [branchId, orderType]);
  useEffect(() => {
    if (!quoteLoading && !quoteError && typeof quote?.minimumLeadMinutes === "number") {
      setLastServerLead(quote.minimumLeadMinutes);
    }
  }, [quote, quoteLoading, quoteError]);
  const currentSlotLead = slotLeadFloor(minLeadMinutes, quoteLoading, lastServerLead);
  useEffect(() => {
    setSlotLead(currentSlotLead);
  }, [currentSlotLead]);

  const minScheduledDate = useMemo(
    () => new Date(Date.now() + minLeadMinutes * 60_000),
    [minLeadMinutes],
  );
  const maxScheduledDate = useMemo(
    () => new Date(Date.now() + 7 * 24 * 60 * 60_000),
    [],
  );
  const minScheduledIso = useMemo(
    () => toDateTimeLocalString(minScheduledDate),
    [minScheduledDate],
  );
  const maxScheduledIso = useMemo(
    () => toDateTimeLocalString(maxScheduledDate),
    [maxScheduledDate],
  );

  const rewardAmount = useRewards ? redemption.redeemableValue : 0;

  // Final pass: the till prices the order with the redemption (service charge
  // base after loyalty; surcharge base after loyalty; redemption after tax,
  // capped at the bill). Display and the create-order payload read from it.
  const tillFinal = useTillPricing({
    menuItems, menuTaxExempt, promotions, surcharges: surchargeDefs, surchargeTaxesById,
    deliveryCoordinates: pricingCoords, loyaltyRedemption: rewardAmount,
  });
  const finalPricing = tillFinal.pricing;
  const appliedSurcharges = finalPricing.appliedSurcharges;
  const surchargeAmount = finalPricing.surchargeAmount;
  const finalServiceTax = finalPricing.serviceFeeAmount;
  const clientTax = finalPricing.taxAmount;
  const finalGrandBeforeLoyalty = finalPricing.totalBeforeLoyalty;
  // A redemption never exceeds the bill (owner ruling 2026-10-10): only the
  // value used comes off (the till's capped loyaltyDiscountAmount), and only its
  // share of points/cashback is burned.
  const usedRedemption = capRedemption(useRewards ? redemption.option : null, finalGrandBeforeLoyalty);
  const usedReward = finalPricing.loyaltyDiscountAmount;
  // The tip is carried on top of the till's total (never part of it).
  const clientTip = Math.round(tipAmt * 100) / 100;
  const clientGrand = Math.round((finalPricing.totalAmount + clientTip) * 100) / 100;
  // payment.amount sent to the provider: the till total (gateways charge amount + tipAmount).
  const clientTotal = finalPricing.totalAmount;

  // Server-priced checkout (spec 2026-09-23 quote-and-lock): once a quote is
  // present, every amount (subtotal, promotion, delivery fee, tax, tip,
  // loyalty redemption, total) comes only from `quotedPricing`. Tip and
  // redemption are inputs to the quote, never layered on top of it here.
  // There is NO fallback to client pricing while the quote is loading or
  // failed; callers must gate on `priceQuote`/`priceQuoteBusy`/`priceQuoteFailed`.
  const quoteDiscount = (quotedPricing?.promotionAmount ?? 0) + (quotedPricing?.discountAmount ?? 0);
  const quoteDeliveryFee = quotedPricing?.deliveryFeeAmount ?? 0;
  // Dine-in service charge, priced by the quote the way the POS charges it (already in the total).
  const quoteServiceFee = quotedPricing?.serviceFeeAmount ?? 0;
  const quoteTax = quotedPricing?.taxAmount ?? 0;
  const quoteGrand = Math.max(0, quotedPricing?.totalAmount ?? 0);
  const discount = quotedPricing
    ? quoteDiscount
    : Math.round((finalPricing.promotionAmount + finalPricing.discountAmount) * 100) / 100;
  const tax = quotedPricing ? quoteTax : clientTax;
  const grand = quotedPricing ? quoteGrand : clientGrand;
  // Payment amount excludes the tip (gateways charge amount + tipAmount).
  const total = quotedPricing ? Math.max(0, Math.round((quoteGrand - quotedTip) * 100) / 100) : clientTotal;
  const payTip = quotedPricing ? quotedTip : requestedTip;
  const displayedSurcharges = (priceQuote?.appliedSurcharges as typeof appliedSurcharges | undefined) ?? appliedSurcharges;

  function routeWithStorefrontBase(path: string): string {
    if (typeof window === "undefined") return path;
    const slug = branchConfig?.urlPath ?? branchSlug ?? "";
    const firstSegment = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
    const basePath = slug && firstSegment === slug ? `/${slug}` : "";
    return `${basePath}${path.startsWith("/") ? path : `/${path}`}`;
  }

  function buildPaymentReturnUrl(orderId: string): string {
    const path =
      orderType === OrderTypeEnum.DineIn
        ? `/dine-in-order/${encodeURIComponent(orderId)}`
        : `/order-tracking/${encodeURIComponent(orderId)}`;
    const url = new URL(routeWithStorefrontBase(path), window.location.origin);
    url.searchParams.set("paymentReturn", "1");
    if (selectedFamily !== "cash") url.searchParams.set("provider", selectedFamily);
    return url.toString();
  }

  // Promise Time Phase 2: bookable slots from quote-eta (operating hours, branch timezone,
  // lead time, slot capacity). Fetched when "Schedule" is chosen and refetched when the
  // basket or fulfilment changes, or after a SLOT_FULL refusal. A failure or an empty
  // list leaves `slots` empty, which keeps the legacy picker: checkout never blocks.
  const slotCartKey = cart.map((l) => `${l.itemId}:${l.qty}`).join("|");
  useEffect(() => {
    if (isDineIn || timeMode !== "scheduled" || !branchId || cart.length === 0) return;
    let cancelled = false;
    setSlotsLoading(true);
    fetchSlots((body) => quoteApi.quote(body), {
      branchId,
      orderType,
      lines: cart.map((l) => ({ itemId: l.itemId, quantity: l.qty })),
      deliveryAreaId,
    })
      .then((next) => {
        if (cancelled) return;
        setSlots(next);
        setSelectedSlot((current) =>
          current && next.some((s) => s.slotKey === current.slotKey) ? current : null,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[Checkout] slot quote failed; keeping the legacy time picker", err);
        setSlots([]);
        setSelectedSlot(null);
      })
      .finally(() => {
        if (cancelled) return;
        setSlotsLoading(false);
        setSlotsLoaded(true);
        setSlotsFetchedAt(Date.now());
        setNowMs(Date.now());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDineIn, timeMode, branchId, orderType, deliveryAreaId, slotCartKey, slotsRefresh]);

  // I1: tick the clock while "Schedule" is open, so slots that slip inside the lead
  // floor disappear and a list older than 5 minutes is refetched.
  useEffect(() => {
    if (isDineIn || timeMode !== "scheduled") return;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [isDineIn, timeMode]);
  useEffect(() => {
    if (isDineIn || timeMode !== "scheduled" || slotsLoading) return;
    if (slotListIsStale(slotsFetchedAt, nowMs)) setSlotsRefresh((n) => n + 1);
  }, [isDineIn, timeMode, slotsLoading, slotsFetchedAt, nowMs]);
  // I1: a picked slot that is now too soon (or past) is dropped, the list is re-quoted
  // and the customer is told to pick again. The cart is untouched.
  useEffect(() => {
    if (isDineIn || timeMode !== "scheduled" || !selectedSlot || slotLead === null) return;
    if (slotMeetsLead(selectedSlot, slotLead, nowMs)) return;
    setSelectedSlot(null);
    setSlotsRefresh((n) => n + 1);
    setError(t("v2.checkout.err_slot_too_soon", "That time is too soon now. Please pick another time."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDineIn, timeMode, selectedSlot, slotLead, nowMs]);

  const checkoutBlocker = useMemo<CheckoutBlocker | null>(() => {
    if (cart.length === 0) return { kind: "cart", message: t("v2.checkout.err_cart_empty") };
    if (!isLoggedIn) return { kind: "signin", message: t("v2.checkout.sign_in_before_order") };
    if (!isDineIn && requiresBranchSelection) {
      return { kind: "branch", message: t("v2.checkout.err_branch_required") };
    }
    if (!isDineIn && isClosed && timeMode === "asap") {
      return {
        kind: "closed",
        message: opensAt
          ? t("v2.checkout.err_branch_closed_opens", { time: opensAt })
          : t("v2.checkout.err_branch_closed"),
      };
    }
    if (!isDineIn && mode === "delivery" && !selectedAddress) {
      return { kind: "address", message: t("v2.checkout.err_pick_address") };
    }
    if (!isDineIn && timeMode === "scheduled" && useSlots) {
      // The server built the slot list (hours, timezone, lead time, capacity): nothing
      // to check here beyond "a slot is picked".
      if (!selectedSlot) {
        return {
          kind: "time",
          message: t("v2.checkout.err_pick_scheduled_time", "Please select a scheduled time"),
        };
      }
    } else if (!isDineIn && timeMode === "scheduled") {
      if (!scheduledTime) {
        return {
          kind: "time",
          message: t(
            "v2.checkout.err_pick_scheduled_time",
            "Please select a scheduled time",
          ),
        };
      }
      const pickedDate = new Date(scheduledTime);
      if (Number.isNaN(pickedDate.getTime())) {
        return {
          kind: "time",
          message: t(
            "v2.checkout.err_pick_scheduled_time",
            "Please select a scheduled time",
          ),
        };
      }
      const nowMs = Date.now();
      const minAllowedMs = nowMs + minLeadMinutes * 60_000;
      if (pickedDate.getTime() < minAllowedMs) {
        return {
          kind: "time",
          message: t("v2.checkout.err_scheduled_min_time", {
            min: minLeadMinutes,
            defaultValue: `Scheduled time must be at least ${minLeadMinutes} minutes in advance.`,
          }),
        };
      }
      const maxAllowedMs = nowMs + 7 * 24 * 60 * 60_000;
      if (pickedDate.getTime() > maxAllowedMs) {
        return {
          kind: "time",
          message: t(
            "v2.checkout.err_scheduled_max_time",
            "Scheduled time cannot be more than 7 days in advance.",
          ),
        };
      }
      if (branch && !isBranchOpenAt(branch, pickedDate)) {
        return {
          kind: "time",
          message: t(
            "v2.checkout.err_scheduled_closed_time",
            "The branch is closed at the selected time. Please choose another time.",
          ),
        };
      }
    }
    if (!branchId) return { kind: "branch_loading", message: t("v2.checkout.err_branch_loading") };
    if (!selectedMethod) return { kind: "payment", message: t("v2.checkout.err_pick_payment") };
    return null;
  }, [
    branch,
    branchId,
    cart.length,
    isClosed,
    isDineIn,
    isLoggedIn,
    minLeadMinutes,
    mode,
    opensAt,
    requiresBranchSelection,
    selectedAddress,
    selectedMethod,
    timeMode,
    scheduledTime,
    useSlots,
    selectedSlot,
    t,
  ]);

  // Appending to an open table order isn't "placing an order" — nothing is
  // paid here, the line just joins the running bill.
  const isAppending = !!appendOrderId && groupCart.status !== "active";
  const placeOrderLabel = isAppending
    ? t("v2.checkout.add_to_order", "Add to order")
    : checkoutBlocker
    ? checkoutBlocker.kind === "signin"
      ? t("v2.checkout.sign_in_to_continue")
      : checkoutBlocker.kind === "address"
      ? t("v2.checkout.choose_address")
      : checkoutBlocker.kind === "branch"
      ? t("v2.checkout.choose_branch")
      : checkoutBlocker.kind === "payment"
      ? t("v2.checkout.choose_payment")
      : checkoutBlocker.kind === "cart"
      ? t("v2.checkout.cart_empty_cta")
      : injectPrice(t("v2.checkout.place_order", { total: "___PRICE___" }), grand)
    : injectPrice(t("v2.checkout.place_order", { total: "___PRICE___" }), grand);

  function finishSuccess(order: OrderOutput) {
    const paymentLabel = selectedMethod
      ? getDisplayName(selectedMethod.displayName, i18n.language || "en") || selectedMethod.id
      : "";
    track("order_paid", {
      order_id: order.id ?? null,
      total: grand,
      currency,
      method: selectedMethod?.id ?? null,
    });
    clearCart();
    cleanupPaymob(PAYMENT_SHEET_ID);
    cleanupFawry();
    if (stripeMountRef.current) {
      stripeMountRef.current.unmount();
      stripeMountRef.current = null;
    }
    setPaymentSheet(null);
    // Solo dine-in (table set, no active group cart) → land on the table
    // bill instead of the receipt success screen. Splitting the bill is an
    // opt-in action, not a forced destination: DineInOrderScreen carries its
    // own explicit "Pay my share" CTA to /orders/{id}/split, so the split
    // flow stays one deliberate tap away rather than something checkout
    // routes the diner into automatically.
    if (isDineIn && order.id && groupCart.status !== "active") {
      navigate(`/dine-in-order/${encodeURIComponent(order.id)}`, {
        state: {
          paymentLabel,
          orderType,
        },
      });
      return;
    }
    navigate("/success", {
      state: {
        orderId: order.id,
        // Empty until the POS claims the order and assigns its number. Pass
        // nothing rather than falling back to the order id — a raw uuid is
        // not an order number and reads as garbage to the customer; the
        // success screen shows "not yet assigned" instead.
        displayOrderNum: order.orderNumber
          ? order.orderNumber.split("-").pop()
          : "",
        eta: etaMax ?? etaMin ?? 0,
        // A Mode A queued create returns a queue ack without the order doc, so
        // fall back to the quote the customer accepted (same inputs, same
        // engine, milliseconds apart).
        promise: order.promise ?? quote?.promise ?? null,
        promiseVisible: quote?.visibleToCustomers === true,
        total: grand,
        paymentLabel,
        orderType,
      },
    });
  }

  async function confirmPaymentOnBackend(
    order: OrderOutput,
    intent: PaymentIntent,
    transactionRef: string,
    raw?: Record<string, unknown>,
  ) {
    if (!order.id) return;
    try {
      await paymentApi.confirmPayment(order.id, {
        intentId: intent.id ?? intent.transactionRef ?? transactionRef,
        transactionRef,
        providerKey: intent.providerKey,
        raw,
      });
    } catch (err) {
      console.warn("[Checkout] confirm-payment failed", err);
    }
  }

  async function verifyFawryOnBackend(order: OrderOutput, intent: PaymentIntent) {
    if (!order.id) return;
    const merchantRefNum =
      (intent.metadata?.merchantRefNum as string | undefined) ||
      (intent.metadata?.transactionRef as string | undefined) ||
      intent.transactionRef ||
      order.id;
    try {
      await paymentApi.fawryVerify(order.id, merchantRefNum);
    } catch (err) {
      console.warn("[Checkout] fawry verify failed; webhook may still process", err);
    }
  }

  function buildPaymentIntentRequest(order: OrderOutput, returnUrl: string): PaymentIntentCreatePayload {
    const nameParts = (user?.name ?? "").trim().split(/\s+/).filter(Boolean);
    const [firstName = "", ...restName] = nameParts;
    // Save-card is currently Fawry-only (Stripe / Paymob have their own
    // saved-card affordances). Only forward the flag when no existing
    // saved card is in play and the user explicitly opted in.
    const includeSaveCard =
      saveCardOptIn && !savedCardId && selectedFamily === "fawry" && !!user?.id;
    return {
      orderId: order.id ?? "",
      tenantId: branchConfig?.tenantId ?? "",
      businessEntityId: "",
      branchId,
      paymentMethodId: selectedMethod?.id ?? "",
      payment: {
        amount: total,
        tipAmount: payTip || undefined,
        currency,
        metadata: {
          returnUrl,
          redirectUrl: returnUrl,
          ...(savedCardId ? { savedCardId } : {}),
          ...(includeSaveCard ? { saveCardInfo: true } : {}),
        },
      },
      customer: {
        id: user?.id ?? undefined,
        firstName,
        lastName: restName.join(" "),
        phone: user?.phone ?? "",
        email: (user as { email?: string | null } | null)?.email ?? "",
      },
      urlPath: branchConfig?.urlPath,
      returnUrl,
      redirectUrl: returnUrl,
      orderType,
      ...(savedCardId ? { savedCardId } : {}),
    };
  }

  function handlePaymentFailure(message?: string) {
    cleanupPaymob(PAYMENT_SHEET_ID);
    cleanupFawry();
    if (stripeMountRef.current) {
      stripeMountRef.current.unmount();
      stripeMountRef.current = null;
    }
    if (paymentSheet) {
      setFailedPayment(paymentSheet);
    }
    setPaymentSheet(null);
    setError(message ?? t("v2.checkout.payment.err_failed_retry"));
  }

  function onRetryPayment() {
    if (!failedPayment) return;
    setError(null);
    const next = failedPayment;
    setFailedPayment(null);
    setPaymentSheet(next);
  }

  function onCancelAndRestart() {
    setFailedPayment(null);
    setError(null);
    navigate("/cart");
  }

  function handleCheckoutBlocker(): boolean {
    if (!checkoutBlocker) return false;
    setError(checkoutBlocker.message);
    if (checkoutBlocker.kind === "signin") {
      setSignInOpen(true);
    } else if (checkoutBlocker.kind === "address" && isLoggedIn && addresses.length === 0) {
      navigate("/addresses/new?returnTo=checkout");
    } else if (checkoutBlocker.kind === "branch") {
      navigate(mode === "pickup" ? "/" : "/specify-location?returnTo=checkout");
    } else if (checkoutBlocker.kind === "cart") {
      navigate("/cart");
    }
    return true;
  }

  function rememberPaymentReturn(
    order: OrderOutput,
    intent: PaymentIntent,
    family: ProviderFamily,
  ) {
    if (!order.id) return;
    savePendingPaymentReturn(
      {
        orderId: order.id,
        intentId: intent.id ?? intent.transactionRef ?? intentRef(intent),
        transactionRef: intentRef(intent),
        providerKey: intent.providerKey ?? null,
        providerFamily: family,
        createdAt: Date.now(),
      },
      branchSlug,
    );
  }

  function redirectUrlFromIntent(intent: PaymentIntent): string | null {
    const metadata = intent.metadata ?? {};
    const raw =
      metadata.redirect_url ??
      metadata.redirection_url ??
      metadata.redirectUrl;
    return typeof raw === "string" && raw ? raw : null;
  }

  async function handleSavedCardIntent(
    order: OrderOutput,
    intent: PaymentIntent,
    family: ProviderFamily,
    returnUrl: string,
  ): Promise<boolean> {
    if (!savedCardId || family === "cash") return false;

    const redirectUrl = redirectUrlFromIntent(intent);
    if (isActionRequired(intent) && redirectUrl) {
      rememberPaymentReturn(order, intent, family);
      window.location.href = redirectUrl;
      return true;
    }

    if (family === "stripe" && intent.clientSecret && intent.publicKey) {
      rememberPaymentReturn(order, intent, family);
      const result = await confirmStripeSavedCardPayment(
        intentToProviderInput(intent),
        returnUrl,
      );
      if (!result.ok) {
        setError(result.error ?? t("v2.checkout.err_saved_card_failed"));
        return true;
      }
      await confirmPaymentOnBackend(order, intent, intentRef(intent));
      finishSuccess(order);
      return true;
    }

    setError(t("v2.checkout.err_saved_card_failed"));
    return true;
  }

  /**
   * Dine-in "add more items": the table already has an open order, so the
   * cart must be *appended* to it, never turned into a second order (the
   * backend rejects that with a 409 — the bug this branch exists to fix).
   *
   * Every menu entry point (item detail, quick-add, rec strips, reorder)
   * funnels through checkout, so this is the one choke point where the
   * append has to be honoured.
   */
  async function appendCartToOpenOrder(targetOrderId: string) {
    if (!isLoggedIn) {
      setError(t("v2.checkout.sign_in_before_order"));
      setSignInOpen(true);
      return;
    }

    const linesToSend = [...cart];
    const totalLines = linesToSend.length;
    let added = 0;

    placingRef.current = true;
    setPlacing(true);
    try {
      // Sequential on purpose: line order matters on the ticket, and a
      // mid-batch failure must leave a recoverable cart rather than an
      // unknown partial state. Each line is dropped from the cart as soon
      // as the backend accepts it, so a retry only re-sends what's left.
      for (const line of linesToSend) {
        await orderApi.addLine(targetOrderId, cartLineToOrderLineInput(line, added + 1));
        removeLine(line.id);
        added += 1;
      }
    } catch (err) {
      console.warn("[Checkout] append to open order failed", err);
      track("order_append_failed", {
        order_id: targetOrderId,
        lines_added: added,
        lines_total: totalLines,
        error: err instanceof Error ? err.message : String(err),
      });
      if (err instanceof ApiError && err.status === 401) {
        // The BFF's POST /orders/{id}/lines requires a customer JWT; an
        // anonymous payer-token diner cannot append today. Surface the
        // sign-in state rather than a raw 401.
        setError(
          t("v2.checkout.append_sign_in_required", {
            defaultValue: "Please sign in to add items to this table's order.",
          }),
        );
        setSignInOpen(true);
      } else if (added > 0) {
        setError(
          t("v2.checkout.append_partial_failed", {
            defaultValue:
              "Added {{added}} of {{total}} items to your table order. The rest are still in your cart — please try again.",
            added,
            total: totalLines,
          }),
        );
      } else {
        setError(
          err instanceof Error && err.message
            ? err.message
            : t("v2.checkout.append_failed", {
                defaultValue: "Couldn't add your items to the table order. Please try again.",
              }),
        );
      }
      return;
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }

    track("order_lines_appended", {
      order_id: targetOrderId,
      lines_count: totalLines,
    });
    setAppendOrderId(null);
    clearCart();
    // The bill is settled separately at the table — no payment/intent work
    // belongs on this path, so land straight on the open order.
    navigate(`/dine-in-order/${encodeURIComponent(targetOrderId)}`);
  }

  async function onPlace() {
    setError(null);
    if (placingRef.current) return;
    // Append path runs before any create/payment logic. Skipped when a group
    // cart is active — that flow already posts its lines server-side.
    if (appendOrderId && cart.length > 0 && groupCart.status !== "active") {
      await appendCartToOpenOrder(appendOrderId);
      return;
    }
    if (handleCheckoutBlocker()) return;
    // I1: re-validate the picked slot against the lead floor at the moment of submit
    // (the list was built server-side at fetch time, and order-service does not enforce
    // the lead on create). Too soon or past: re-quote and ask again, cart kept.
    if (
      !isDineIn &&
      timeMode === "scheduled" &&
      useSlots &&
      selectedSlot &&
      !slotMeetsLead(selectedSlot, currentSlotLead, Date.now())
    ) {
      setSelectedSlot(null);
      setNowMs(Date.now());
      setSlotsRefresh((n) => n + 1);
      setError(t("v2.checkout.err_slot_too_soon", "That time is too soon now. Please pick another time."));
      return;
    }
    if (orderType === OrderTypeEnum.Delivery && minOrder > 0 && subtotal < minOrder) {
      setError(
        injectPrice(
          t("v2.cart.min_order_gap_notice", {
            min: "___PRICE___",
            defaultValue: "Minimum order for delivery is ___PRICE___",
          }),
          minOrder
        ) as string
      );
      return;
    }
    if (!branchId || !selectedMethod) return;
    // Server-priced checkout (D3): a fresh quote is required to create an order.
    // The button is disabled for this same condition, so this is a defensive
    // no-op guard, not the primary gate.
    // Shadow mode prices locally and never needs a quote.
    if (enforceQuote && !priceQuote) return;
    // Enforce mode: the quote placed with the order. Null in shadow mode.
    const placedQuote = enforceQuote ? priceQuote : null;

    placingRef.current = true;
    setPlacing(true);
    try {
      const orderId = makeOrderId();
      const paymentReturnUrl = buildPaymentReturnUrl(orderId);
      // The quote's lines — including any server-granted free-item lines —
      // are what gets created. order-service ignores a quoted order's own
      // orderLines/pricingSummary and re-derives them from the stored quote,
      // but they're sent anyway so logs/pos_v2 previews stay consistent.
      // Shadow mode: the cart lines as priced locally (production behaviour).
      // Shadow: the cart lines with the till's line amounts, plus the free-item
      // lines the till granted.
      const pricedByCartIndex = new Map(
        finalPricing.lines.filter((l) => l.cartIndex != null).map((l) => [l.cartIndex as number, l]),
      );
      const orderLines: OrderLineInput[] = !placedQuote
        ? [
            ...cart.map((line, idx) => cartLineToOrderLineInput(line, idx + 1, pricedByCartIndex.get(idx))),
            ...finalPricing.lines
              .filter((l) => !!l.grantedByPromotionId)
              .map((l, i) =>
                grantedLineToOrderLineInput(l, menuItems.find((m) => m.id === l.itemId), cart.length + i + 1),
              ),
          ]
        : placedQuote.orderLines.map((line) => ({
        indexId: line.indexId ?? undefined,
        lineId: line.lineId ?? undefined,
        itemId: line.itemId,
        itemName: line.itemName ?? { en: line.itemId },
        quantity: line.quantity,
        unitPrice: line.unitPrice ?? null,
        selectedVariants: line.selectedVariants ?? [],
        selectedModifiers: line.selectedModifiers ?? [],
        pricingSummary: (line.pricingSummary as OrderLineInput["pricingSummary"]) ?? {
          baseAmount: 0,
          subtotalAmount: 0,
          lineTotalBeforeTax: 0,
          totalAmount: 0,
        },
        // The quote's item taxes travel with the line: a POS-paired branch
        // stores the lines as sent, and an untaxed line stays untaxed.
        taxInfo: line.taxInfo ?? [],
        notes: line.notes ?? null,
        ...(line.grantedByPromotionId ? { grantedByPromotionId: line.grantedByPromotionId } : {}),
      })) as OrderLineInput[];

      // Save-card opt-in (Fawry only — Stripe/Paymob have their own
      // saved-card affordances). Forwarded as ``saveCardInfo`` so the
      // payment-service Fawry gateway and webhook handler pick it up.
      const includeSaveCard =
        saveCardOptIn && !savedCardId && selectedFamily === "fawry" && !!user?.id;

      const paymentBlock: PaymentPayloadInput = {
        method: selectedMethod.id,
        amount: total,
        tipAmount: payTip || undefined,
        currency,
        metadata:
          selectedFamily === "cash"
            ? null
            : {
                providerKey: selectedFamily,
                returnUrl: paymentReturnUrl,
                redirectUrl: paymentReturnUrl,
                ...(savedCardId ? { savedCardId } : {}),
                ...(includeSaveCard ? { saveCardInfo: true } : {}),
              },
      };

      // Inbound referral (S3): the referrer's `?ref=` code captured on landing.
      // Loyalty (Plan 2) resolves referral attribution server-side; the client
      // only carries it through. Cleared after a successful order so it
      // attributes exactly once.
      const referralCode = getReferralCode();

      // Program-neutral loyalty EARN signals an applied promotion contributes
      // (`{ earnMultiplier?, loyaltyCredit? }`). Today's order/item-discount
      // promos yield `{}`; only earn_multiplier / loyalty_credit rewards
      // populate them. The client never resolves points vs cashback — loyalty
      // does (frozen contract).
      const loyaltySignals = placedQuote
        ? appliedPromotion
          ? promotionLoyaltySignals(appliedPromotion)
          : {}
        : finalPricing.loyaltySignals;

      const redeemed = placedQuote
        ? {
            programId: placedQuote.loyaltyProgramId ?? null,
            points: placedQuote.loyaltyPointsRedeemed ?? 0,
            cashback: placedQuote.loyaltyCashbackRedeemed ?? 0,
          }
        : {
            programId: redemptionInput?.loyaltyProgramId ?? null,
            points: redemptionInput ? usedRedemption.points : 0,
            cashback: redemptionInput ? usedRedemption.cashback : 0,
          };

      const payload = {
        id: orderId,
        orderNumber: "",
        transactionNumber: "",
        businessEntityId: "",
        brandId: branchConfig?.brandId ?? null,
        branchId,
        branchName: (branch as any)?.name 
          ? { en: (branch as any).name.en || (branch as any).name.ar, ar: (branch as any).name.ar || (branch as any).name.en } 
          : (branch as any)?.branchName 
            ? { en: (branch as any).branchName.en || (branch as any).branchName.ar, ar: (branch as any).branchName.ar || (branch as any).branchName.en }
            : { en: branchDisplayName(branch, i18n.language || "en") || branchId },
        channel: ChannelEnum.Online,
        orderType,
        // Menu-link branch config can pin a posInstance; order-service routes
        // the order (and its POS ordersQueue write) there instead of the
        // branch-default POS. Null → server falls back to the default.
        pointOfSaleId:
          branchConfig?.branches?.find((b) => b.branchId === branchId)?.posId ??
          null,
        tableId: isDineIn ? tableInfo?.tableId ?? null : null,
        floorId: isDineIn ? tableInfo?.floorId ?? null : null,
        customerId: user?.id ?? null,
        customerName: user?.name ?? null,
        customerPhone: user?.phone ?? null,
        currency,
        scheduledTimestamp: scheduledIso,
        deliveryAddress:
          !isDineIn && mode === "delivery" && selectedAddress
            ? toAddressInput(selectedAddress)
            : null,
        deliveryAreaId,
        deliveryInstructions:
          !isDineIn && mode === "delivery" && contactless
            ? "Contactless delivery — leave at door"
            : null,
        notes: orderNote.trim() ? orderNote.trim() : null,
        // Server-priced checkout (D3): every amount, tip and loyalty redemption
        // included, comes from the stored quote. order-service stores the quote
        // and 409s PRICE_CHANGED if tip/redemption here differ from it.
        //
        // Shadow mode: totals are the local pricing (production behaviour). No
        // taxBreakdown is sent, so the BFF derives it from the tenant's taxes
        // (item + service-fee + delivery), summing to this same taxAmount.
        pricingSummary: !placedQuote
          ? {
              currency,
              // The till's amounts (priceCart); the tip rides on top of its total.
              itemsSubtotal: finalPricing.itemsSubtotal,
              promotionAmount: finalPricing.promotionAmount,
              ...(finalPricing.discountAmount > 0 ? { discountAmount: finalPricing.discountAmount } : {}),
              deliveryFeeAmount: finalPricing.deliveryFeeAmount,
              serviceFeeAmount: isDineIn ? finalServiceTax : undefined,
              taxAmount: finalPricing.taxAmount,
              tipAmount: requestedTip,
              loyaltyDiscountAmount: usedReward > 0 ? usedReward : undefined,
              totalAmount: grand,
              ...(surchargeAmount > 0 ? { surchargeAmount } : {}),
            }
          : {
          currency,
          itemsSubtotal: placedQuote.pricingSummary.itemsSubtotal ?? subtotal,
          promotionAmount: discount,
          deliveryFeeAmount: placedQuote.pricingSummary.deliveryFeeAmount ?? deliveryFee,
          serviceFeeAmount: placedQuote.pricingSummary.serviceFeeAmount ?? 0,
          taxAmount: tax,
          // Per tax and what it applied to (item VAT, delivery VAT), as quoted.
          taxBreakdown: placedQuote.pricingSummary.taxBreakdown ?? [],
          tipAmount: placedQuote.pricingSummary.tipAmount ?? 0,
          // Redeemed loyalty rewards land on `loyaltyDiscountAmount`, already
          // subtracted from the quote total.
          loyaltyDiscountAmount: quotedReward > 0 ? quotedReward : undefined,
          totalAmount: grand,
          // Task 5 (surcharge module) — hand-added pending a full OpenAPI
          // regen, mirrors the totals `computeCartTotals` already folded
          // into `tax`/`grand` above. `tax` already includes exclusive +
          // included surcharge tax; `grand`/`totalAmount` already includes
          // surchargeAmount + exclusive surcharge tax (see pricing.ts).
          ...(surchargeAmount > 0 ? { surchargeAmount } : {}),
        },
        orderLines,
        // Shadow mode omits quoteId so order-service stays in shadow pricing.
        ...(placedQuote ? { quoteId: placedQuote.quoteId } : {}),
        ...codesPayload(placedQuote ? appliedPromotions.map((p) => p.promoCode) : tillFinal.enteredCodes),
        // The menu the order was rendered and priced from (omitted when unknown).
        ...(menuId ? { menuId } : {}),
        // Task 5 (surcharge module) — hand-added pending a full OpenAPI
        // regen; order-service persists whatever the channel sends and
        // recomputes for log-only validation (v1, per spec §7).
        ...(appliedSurcharges.length > 0 ? { surcharges: appliedSurcharges } : {}),
        appliedPromotionIds: placedQuote
          ? appliedPromotions.some((p) => p.id) ? appliedPromotions.flatMap((p) => (p.id ? [p.id] : [])) : null
          : finalPricing.appliedPromotionIds.length ? finalPricing.appliedPromotionIds : null,
        // Enforce: exactly the redemption the quote priced (order-service
        // compares them). Shadow: the redemption the customer opted into.
        ...(redeemed.points || redeemed.cashback
          ? {
              loyaltyProgramId: redeemed.programId,
              ...(redeemed.points
                ? { loyaltyPointsRedeemed: redeemed.points }
                : { loyaltyCashbackRedeemed: redeemed.cashback }),
            }
          : {}),
        preferPaymentMethod:
          selectedFamily === "cash"
            ? PreferPaymentMethodEnum.Cod
            : PreferPaymentMethodEnum.Card,
        selectedPaymentMethodType: selectedMethod.paymentMethodType ?? null,
        // Rider P1f: COD change-for; omitted after switching to card or when empty/too low.
        ...changeForPayload({ raw: changeForRaw, isCash: showChangeFor, enabled: true, amountDue: grand }),
        payment: paymentBlock,
        // Marketing attribution (S3): UTM / click-id / campaign touches captured
        // on landing. order-service computes `waterfallMethod` server-side.
        // Omitted entirely when there's nothing to attribute.
        ...(hasAttribution() ? { attribution: getAttributionForOrder() } : {}),
        // Every hand-added field (referralCode, loyaltySignals, attribution,
        // surcharges, loyaltyCashbackRedeemed) is now declared on OrderInput,
        // so this payload is type-checked rather than cast. Do NOT reintroduce
        // an `as OrderInput` cast: it silently dropped loyaltyCashbackRedeemed
        // on every cashback-program order.
        ...(referralCode ? { referralCode } : {}),
        ...(Object.keys(loyaltySignals).length ? { loyaltySignals } : {}),
      } satisfies OrderInput;

      const { order, intent: intentFromOrder } = await orderApi.create(payload);

      // Order accepted — consume the inbound referral code so it attributes to
      // this order only (a later order won't re-send the same referrer code).
      if (referralCode) clearReferralCode();

      // Lock the redeemed loyalty rewards against this order so they can't be
      // double-spent before completion (mirrors v1's `holdLoyaltyPoints`).
      // Best-effort — backend treats this as advisory and a dropped call here
      // does not block payment.
      if ((redeemed.points || redeemed.cashback) && order.id && user?.id) {
        const points = redeemed.points;
        const cashback = redeemed.cashback;
        loyaltyApi
          .holdPoints({
            customer_id: user.id,
            points_to_hold: points,
            cashback_to_hold: cashback,
            related_order_id: order.id,
          })
          .catch((err) => {
            console.warn("[Checkout] hold-points failed (non-fatal)", err);
          });
      }

      track("order_created", {
        order_id: order.id ?? null,
        order_number: order.orderNumber ?? null,
        items_count: cart.length,
        units_count: cart.reduce((n, l) => n + l.qty, 0),
        subtotal,
        tip: tipAmt,
        total: grand,
        currency,
        order_type: orderType,
        contactless,
        payment_method: selectedMethod.id,
      });

      if (isLoggedIn && marketingOptIn) {
        consentApi.optInMarketing(true).catch((err) =>
          console.warn("[consent] checkout opt-in failed (non-fatal)", err),
        );
      }

      // Refresh the web-push token at order time so already-opted-in customers
      // keep receiving transactional order-status notifications. SILENT only:
      // we don't prompt here — the permission prompt must be triggered by a
      // user gesture (see PushOptIn on the tracking screen), so a gesture-less
      // request would be suppressed by the browser. When permission is already
      // granted, enablePush() registers without any prompt.
      if (isLoggedIn && typeof Notification !== "undefined" && Notification.permission === "granted") {
        void enablePush();
      }

      let intent = intentFromOrder;
      if (!intent && selectedFamily !== "cash") {
        if (!order.id) {
          throw new Error(t("v2.checkout.err_missing_order_id"));
        }
        intent = await paymentApi.createIntent(buildPaymentIntentRequest(order, paymentReturnUrl));
      }

      // No intent in response → COD / no payment required.
      if (!intent) {
        finishSuccess(order);
        return;
      }

      if (isIntentSuccessful(intent)) {
        await confirmPaymentOnBackend(
          order,
          intent,
          intentRef(intent),
        );
        finishSuccess(order);
        return;
      }

      const providerFamily = classifyProvider(intent);
      const family =
        providerFamily === "unknown" && selectedFamily !== "cash"
          ? selectedFamily
          : providerFamily;

      if (savedCardId) {
        const handled = await handleSavedCardIntent(order, intent, family, paymentReturnUrl);
        if (handled) return;
      }

      const redirectUrl = redirectUrlFromIntent(intent);
      if (isActionRequired(intent) && redirectUrl) {
        rememberPaymentReturn(order, intent, family);
        window.location.href = redirectUrl;
        return;
      }

      if (family === "unknown") {
        throw new Error(t("v2.checkout.err_unknown_payment_provider"));
      }
      setPaymentSheet({ intent, family, order });
    } catch (err) {
      track("order_failed", {
        error: err instanceof Error ? err.message : String(err),
        items_count: cart.length,
        total: grand,
      });
      console.warn("[Checkout] place order failed", err);
      if (isSlotFullError(err)) {
        // Another checkout took the last minutes of this slot (409 SLOT_FULL): drop the
        // choice and reload the list so the full slot disappears. The cart is untouched.
        setSelectedSlot(null);
        setScheduledTime("");
        setSlotsRefresh((n) => n + 1);
        setError(t("v2.checkout.err_slot_full", "That time just filled up. Please pick another time."));
        return;
      }
      if (asapPausedNextSlot(err)) {
        // ASAP was paused between the quote and the order (409 PROMISE_ASAP_PAUSED):
        // switch to "Schedule" so the customer can pick a time. The cart is untouched.
        setTimeMode("scheduled");
        setError(t("v2.checkout.err_asap_paused", "The kitchen is not taking ASAP orders right now. Please pick a time."));
        return;
      }
      if (enforceQuote && isPriceChanged(err)) {
        // The quote went stale between checkout render and this create attempt
        // (D3): show the old→new total, keep the customer on checkout, and
        // require another tap on Place order. Never auto-retry the create.
        const previousGrand = grand;
        const freshQuote = priceChangedQuote(err);
        if (freshQuote) {
          replacePriceQuote(freshQuote);
          setPriceChangedBanner({ from: previousGrand, to: freshQuote.pricingSummary.totalAmount ?? previousGrand });
        } else {
          // No quote came back — re-request one so the customer can try again.
          setPriceChangedBanner({ from: previousGrand, to: previousGrand });
          retryPriceQuote();
        }
        return;
      }
      let msg = t("v2.checkout.err_place_failed");
      if (err && typeof err === "object" && "body" in err) {
        const body = (err as any).body;
        if (body?.detail?.message) {
          msg = body.detail.message;
        } else if (err instanceof Error) {
          msg = err.message;
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      placingRef.current = false;
      setPlacing(false);
    }
  }

  // When the sheet opens for Paymob, mount the Pixel SDK into the container.
  useEffect(() => {
    if (!paymentSheet) return;
    if (paymentSheet.family === "paymob") {
      const timer = setTimeout(() => {
        launchPaymobPixel(intentToProviderInput(paymentSheet.intent), {
          elementId: PAYMENT_SHEET_ID,
          onSuccess: async () => {
            await confirmPaymentOnBackend(
              paymentSheet.order,
              paymentSheet.intent,
              intentRef(paymentSheet.intent),
            );
            finishSuccess(paymentSheet.order);
          },
          onCancel: () => {
            handlePaymentFailure(t("v2.checkout.payment.err_cancelled_retry"));
          },
          onFailure: (err) => {
            console.warn("[Checkout] paymob launch failed", err);
            handlePaymentFailure(t("v2.checkout.payment.err_failed_retry"));
          },
        }).catch((err) => {
          console.warn("[Checkout] paymob promise rejected", err);
          handlePaymentFailure(
            err instanceof Error ? err.message : t("v2.checkout.payment.err_failed_retry"),
          );
        });
      }, 80);
      return () => clearTimeout(timer);
    }
    if (paymentSheet.family === "stripe") {
      const timer = setTimeout(() => {
        if (!paymentSheet.intent.publicKey) {
          setError(t("v2.checkout.err_stripe_missing_key"));
          return;
        }
        mountStripeCardElement(paymentSheet.intent.publicKey, STRIPE_ELEMENT_ID)
          .then((mount) => {
            stripeMountRef.current = mount;
          })
          .catch((err) => {
            console.warn("[Checkout] stripe mount failed", err);
            setError(err instanceof Error ? err.message : t("v2.checkout.err_payment_failed"));
          });
      }, 80);
      return () => clearTimeout(timer);
    }
    if (paymentSheet.family === "fawry") {
      // Fawry pops its own modal — close our sheet immediately.
      launchFawryCheckout(intentToProviderInput(paymentSheet.intent), {
        loadingMessage: t("v2.checkout.payment.fawry_loading"),
        onSuccess: async () => {
          await verifyFawryOnBackend(paymentSheet.order, paymentSheet.intent);
          finishSuccess(paymentSheet.order);
        },
        onFailure: (err) => {
          console.warn("[Checkout] fawry failed", err);
          handlePaymentFailure(t("v2.checkout.payment.err_failed_retry"));
        },
      }).catch((err) => {
        console.warn("[Checkout] fawry launch failed", err);
        handlePaymentFailure(
          err instanceof Error ? err.message : t("v2.checkout.payment.err_failed_retry"),
        );
      });
    }
    return undefined;
  }, [paymentSheet]);

  async function onConfirmStripe() {
    if (!paymentSheet || !stripeMountRef.current) return;
    setConfirming(true);
    setError(null);
    try {
      const result = await confirmStripeCardPayment(
        intentToProviderInput(paymentSheet.intent),
        stripeMountRef.current,
      );
      if (!result.ok) {
        handlePaymentFailure(result.error ?? t("v2.checkout.payment.err_failed_retry"));
        return;
      }
      await confirmPaymentOnBackend(
        paymentSheet.order,
        paymentSheet.intent,
        intentRef(paymentSheet.intent),
      );
      finishSuccess(paymentSheet.order);
    } finally {
      setConfirming(false);
    }
  }

  function onCloseSheet() {
    cleanupPaymob(PAYMENT_SHEET_ID);
    cleanupFawry();
    if (stripeMountRef.current) {
      stripeMountRef.current.unmount();
      stripeMountRef.current = null;
    }
    setPaymentSheet(null);
  }

  // Close the payment sheet on Escape.
  useEffect(() => {
    if (!paymentSheet) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseSheet();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSheet]);

  return (
    <div style={{ paddingBottom: 140 }}>
      <TopBar title={t("v2.checkout.title")} onBack={() => navigate(-1)} />

      <div style={{ padding: "12px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {!isDineIn && (requiresBranchSelection || (isClosed && timeMode === "asap")) && (
          <Card padding={12} style={{ background: "#fff7ed", borderColor: "#fed7aa" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <Icons.info size={16} style={{ color: "#9a3412", marginTop: 1 }} />
              <div style={{ fontSize: 13, color: "#9a3412", fontWeight: 600, lineHeight: 1.45 }}>
                {requiresBranchSelection
                  ? t("v2.checkout.err_branch_required")
                  : opensAt
                  ? t("v2.checkout.err_branch_closed_opens", { time: opensAt })
                  : t("v2.checkout.err_branch_closed")}
              </div>
            </div>
          </Card>
        )}

        {checkoutBlocker &&
          checkoutBlocker.kind !== "branch" &&
          checkoutBlocker.kind !== "closed" && (
            <Card padding={12} style={{ background: "#eff6ff", borderColor: "#bfdbfe" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <Icons.info size={16} style={{ color: "#1d4ed8", marginTop: 1 }} />
                <div style={{ fontSize: 13, color: "#1e40af", fontWeight: 600, lineHeight: 1.45 }}>
                  {checkoutBlocker.message}
                </div>
              </div>
            </Card>
          )}

        {isDineIn ? (
          <Card padding={0}>
            <div style={cardHeader}>
              <Icons.store size={16} style={{ color: "var(--primary-700)" }} />
              <span>{t("v2.checkout.dine_in")}</span>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 4 }}>
                {t("v2.checkout.your_table")}
              </div>
              <div className="display" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)" }}>
                {tableInfo?.tableName ??
                  (tableInfo?.tableId
                    ? t("v2.checkout.table_short", { id: tableInfo.tableId.slice(0, 4) })
                    : t("v2.checkout.table_default"))}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }}>
                {t("v2.checkout.order_to_table")}
              </div>
            </div>
          </Card>
        ) : mode === "delivery" ? (
          <Card padding={0}>
            <div style={cardHeader}>
              <Icons.pin size={16} style={{ color: "var(--primary-700)" }} />
              <span>{t("v2.checkout.delivery_address")}</span>
              {/* Address change disabled by request
              <button
                style={editBtn}
                onClick={() => {
                  if (!isLoggedIn) setSignInOpen(true);
                  else navigate("/addresses/new?returnTo=checkout");
                }}
              >
                <Icons.plus size={13} /> {t("v2.checkout.add")}
              </button>
              */}
            </div>
            {!isLoggedIn && (
              <div style={{ padding: "14px", fontSize: 13, color: "var(--ink-2)" }}>
                {t("v2.checkout.sign_in_to_use_address")}
              </div>
            )}
            {isLoggedIn && addressesLoading && addresses.length === 0 && (
              <div style={{ padding: 14, fontSize: 13, color: "var(--ink-3)" }}>
                {t("v2.checkout.loading_addresses")}
              </div>
            )}
            {isLoggedIn && !addressesLoading && addresses.length === 0 && (
              <div style={{ padding: 14, fontSize: 13, color: "var(--ink-2)" }}>
                {t("v2.checkout.no_addresses")}
              </div>
            )}
            {addresses.map((a, i) => (
              <button
                key={a.id}
                disabled
                style={{
                  ...optionRow,
                  borderTop: i === 0 ? 0 : "1px solid var(--border)",
                  cursor: "default",
                  opacity: selectedAddressId === a.id ? 1 : 0.5,
                }}
              >
                <Radio on={selectedAddressId === a.id} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span className="display" style={{ fontWeight: 700, fontSize: 14 }}>
                      {a.name || t("v2.checkout.address_label_default")}
                    </span>
                    {a.isPrimary && <Badge tone="primary">{t("v2.checkout.default")}</Badge>}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 2 }}>
                    {shortLine(a)}
                  </div>
                  {a.city && (
                    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                      {[a.city, a.state, a.postalCode].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </Card>
        ) : (
          <Card padding={0}>
            <div style={cardHeader}>
              <Icons.store size={16} style={{ color: "var(--primary-700)" }} />
              <span>{t("v2.checkout.pickup_branch")}</span>
              <button style={editBtn} onClick={() => navigate("/?openFulfillment=pickup")}>
                {t("v2.common.change")}
              </button>
            </div>
            <div style={{ padding: 14 }}>
              <div className="display" style={{ fontSize: 15, fontWeight: 800, color: "var(--ink-1)" }}>
                {branchDisplayName(branch, i18n.language || "en") || branchId}
              </div>
              <div style={{ fontSize: 13, color: "var(--ink-2)", marginTop: 4 }}>
                {branchAddressLine(branch, i18n.language || "en") || t("v2.checkout.pickup_at_counter")}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 8 }}>
                {t("v2.checkout.pickup_note")}
              </div>
            </div>
          </Card>
        )}

        {!isDineIn && (
          <Card padding={0}>
            <div style={cardHeader}>
              <Icons.clock size={16} style={{ color: "var(--primary-700)" }} />
              <span>{t("v2.checkout.when")}</span>
            </div>
            <div style={{ padding: "0 14px 14px" }}>
              <button
                onClick={() => setTimeMode("asap")}
                style={{ ...optionRow, padding: "12px 0", borderBottom: "1px solid var(--border)" }}
              >
                <Radio on={timeMode === "asap"} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="display" style={{ fontSize: 14, color: "var(--ink-1)", fontWeight: 700 }}>
                    {t("v2.checkout.asap")}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2, fontWeight: 500 }}>
                    {asapQuoteText ??
                      (etaMin != null && etaMax != null
                        ? t("v2.checkout.asap_min", { min: etaMin, max: etaMax })
                        : null)}
                  </div>
                </div>
              </button>
              
              {timeMode === "scheduled" && (useSlots || (slotsLoading && !slotsLoaded)) ? (
                <>
                  <button
                    onClick={() => setTimeMode("scheduled")}
                    style={{ ...optionRow, padding: "12px 0" }}
                  >
                    <Radio on />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="display" style={{ fontSize: 14, color: "var(--ink-1)", fontWeight: 700 }}>
                        {t("v2.checkout.schedule")}
                      </div>
                    </div>
                  </button>
                  {/* Promise Time Phase 2: server slots. Outside the option button because a
                      <button> cannot contain the picker's buttons. */}
                  <div style={{ paddingBottom: 4 }}>
                    <SlotPicker
                      slots={visibleSlots}
                      loading={slotsLoading && !useSlots}
                      value={selectedSlot?.slotKey ?? null}
                      onChange={setSelectedSlot}
                    />
                    {selectedSlot && scheduledQuoteText && (
                      <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 6, fontWeight: 500 }}>
                        {scheduledQuoteText}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setTimeMode("scheduled")}
                  style={{ ...optionRow, padding: "12px 0" }}
                >
                  <Radio on={timeMode === "scheduled"} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="display" style={{ fontSize: 14, color: "var(--ink-1)", fontWeight: 700 }}>
                      {t("v2.checkout.schedule")}
                    </div>
                    {timeMode === "scheduled" && (
                      <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="datetime-local"
                          value={scheduledTime}
                          min={minScheduledIso}
                          max={maxScheduledIso}
                          onChange={(e) => setScheduledTime(e.target.value)}
                          style={{
                            padding: "8px 12px",
                            borderRadius: 8,
                            border: "1px solid var(--border)",
                            background: "var(--surface-1)",
                            fontFamily: "inherit",
                            color: "var(--ink-1)",
                            fontSize: 14,
                            width: "100%",
                            boxSizing: "border-box",
                          }}
                        />
                        {scheduledQuoteText && (
                          <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 6, fontWeight: 500 }}>
                            {scheduledQuoteText}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              )}
            </div>
          </Card>
        )}

        <Card padding={0}>
          <div style={cardHeader}>
            <Icons.card size={16} style={{ color: "var(--primary-700)" }} />
            <span>{t("v2.checkout.payment_label")}</span>
            <button style={editBtn} onClick={() => navigate("/payments/new")}>
              <Icons.plus size={13} /> {t("v2.checkout.add")}
            </button>
          </div>
          {allowedMethods.length === 0 && (
            <div style={{ padding: 14, fontSize: 13, color: "var(--ink-3)" }}>
              {t("v2.checkout.no_payment_methods")}
            </div>
          )}
          {allowedMethods.map((m, i) => {
            const family = classifyMethod(m);
            return (
              <button
                key={m.id}
                onClick={() => setPay(m.id)}
                style={{
                  ...optionRow,
                  borderTop: i === 0 ? 0 : "1px solid var(--border)",
                }}
              >
                <Radio on={pay === m.id} />
                <div
                  style={{
                    width: 38,
                    height: 26,
                    borderRadius: 6,
                    background:
                      family === "cash" ? "#16a34a" : family === "fawry" ? "#fbbf24" : "#1a1f71",
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {family === "cash" ? (
                    <Icons.wallet size={15} style={{ color: "#fff" }} />
                  ) : family === "fawry" ? (
                    <span style={{ color: "#1f2937" }}>FAW</span>
                  ) : (
                    <Icons.card size={16} style={{ color: "#fff" }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>
                    {getDisplayName(m.displayName, i18n.language || "en") || m.id}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    {family === "cash"
                      ? t("v2.checkout.pay_at_door")
                      : family === "paymob"
                      ? t("v2.checkout.card_apple_google")
                      : family === "fawry"
                      ? t("v2.checkout.pay_with_fawry")
                      : family === "stripe"
                      ? t("v2.checkout.card_payment")
                      : m.paymentMethodType ?? ""}
                  </div>
                </div>
              </button>
            );
          })}
          {selectedFamily !== "cash" && savedCards.length > 0 && (
            <div style={{ borderTop: "1px solid var(--border)", padding: 12 }}>
              <div style={{ fontSize: 12, color: "var(--ink-3)", fontWeight: 600, marginBottom: 8 }}>
                {t("v2.checkout.saved_cards")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  onClick={() => setSavedCardId(null)}
                  style={{ ...optionRow, padding: 8 }}
                >
                  <Radio on={savedCardId === null} />
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{t("v2.checkout.use_new_card")}</div>
                </button>
                {savedCards.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSavedCardId(c.id)}
                    style={{ ...optionRow, padding: 8 }}
                  >
                    <Radio on={savedCardId === c.id} />
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {c.brand?.toUpperCase()} •••• {c.last4}
                    </div>
                    {c.expiryMonth && c.expiryYear ? (
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginInlineStart: "auto" }}>
                        {String(c.expiryMonth).padStart(2, "0")}/{String(c.expiryYear).slice(-2)}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          )}
          {selectedFamily === "fawry" && !!user?.id && !savedCardId && (
            <div style={{ borderTop: "1px solid var(--border)", padding: 12 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  fontSize: 13,
                  color: "var(--ink-1)",
                }}
              >
                <input
                  type="checkbox"
                  checked={saveCardOptIn}
                  onChange={(e) => setSaveCardOptIn(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <span style={{ fontWeight: 600 }}>
                  {t("v2.checkout.save_card_for_next_time")}
                </span>
              </label>
            </div>
          )}
        </Card>

        {showChangeFor && (
          <Card>
            <ChangeForField value={changeForRaw} onChange={setChangeForRaw} amountDue={grand} currency={currency} />
          </Card>
        )}

        {isLoggedIn && (
          <Card padding={14}>
            <MarketingConsentToggle compact value={marketingOptIn} onChange={handleMarketingConsentChange} />
          </Card>
        )}

        {tipsEnabled && (
          <Card padding={14}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div>
                <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>{t("v2.checkout.add_a_tip")}</div>
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                  {t(mode === "delivery" ? "v2.checkout.tip_for_courier" : "v2.checkout.tip_for_staff")}
                </div>
              </div>
              <div className="display" style={{ fontSize: 16, fontWeight: 700, color: "var(--primary-700)" }}>
                <Price amount={tipAmt} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
              {[0, 10, 15, 20, 25].map((n) => (
                <button
                  key={n}
                  onClick={() => setTip(n)}
                  style={{
                    padding: "10px 0",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit",
                    border: `1px solid ${tip === n ? "var(--primary-600)" : "var(--border)"}`,
                    background: tip === n ? "var(--primary-600)" : "var(--surface-1)",
                    color: tip === n ? "#fff" : "var(--ink-1)",
                    cursor: "pointer",
                  }}
                >
                  {n === 0 ? t("v2.checkout.none") : `${n}%`}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* {!isDineIn && mode === "delivery" && (
          <Card padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "var(--surface-2)",
                display: "grid",
                placeItems: "center",
                color: "var(--ink-1)",
              }}
            >
              <Icons.shield size={18} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>{t("v2.checkout.contactless_delivery")}</div>
              <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2 }}>
                {t("v2.checkout.contactless_desc")}
              </div>
            </div>
            <Toggle on={contactless} onChange={() => setContactless(!contactless)} />
          </Card>
        )} */}

        {/* What this order earns — highlights an earn-rule bonus (e.g. 100%
            cashback on the first order) so the promise is visible before paying. */}
        {isLoggedIn && earnings.earnsSomething && earnings.estimate && (
          <Card padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "var(--primary-50)",
                display: "grid",
                placeItems: "center",
                color: "var(--primary-700)",
              }}
            >
              <Icons.spark size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>
                {/* The merchant named this rule ("Welcome bonus", "أول طلب");
                    show their wording when they gave one, and only fall back to
                    a generic label when the name is missing for this language. */}
                {getDisplayName(
                  (earnings.estimate.earnRule?.name ?? {}) as { [k: string]: unknown },
                  i18n.language || "en",
                ) ||
                  (earnings.estimate.earnRule
                    ? t("v2.checkout.earn_bonus_title")
                    : t("v2.checkout.earn_title"))}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2 }}>
                {(earnings.estimate.estimatedCashback ?? 0) > 0
                  ? injectPrice(t("v2.checkout.earn_cashback", {
                      defaultValue: "{{amount}} cashback on this order",
                      amount: "___PRICE___",
                    }), earnings.estimate.estimatedCashback)
                  : t("v2.checkout.earn_points", {
                      defaultValue: "{{points}} points on this order",
                      points: earnings.estimate.estimatedPoints,
                    })}
              </div>
            </div>
          </Card>
        )}

        {/* Loyalty rewards toggle — surfaces only when the customer has at
            least one eligible redemption against this draft order. */}
        {isLoggedIn && (redemption.loading || !redemption.disabled) && (
          <Card padding={14} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "var(--primary-50)",
                display: "grid",
                placeItems: "center",
                color: "var(--primary-700)",
              }}
            >
              <Icons.spark size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>
                {t("v2.checkout.use_rewards", { defaultValue: "Use rewards" })}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 2 }}>
                {redemption.loading
                  ? t("v2.checkout.checking_rewards", { defaultValue: "Checking eligible rewards…" })
                  : redemption.option?.type === "points"
                  ? injectPrice(t("v2.checkout.redeem_points", {
                      defaultValue: "Redeem {{points}} pts — saves {{amount}}",
                      points: redemption.option.maxRedeemable ?? 0,
                      amount: "___PRICE___",
                    }), redemption.redeemableValue)
                  : injectPrice(t("v2.checkout.redeem_cashback", {
                      defaultValue: "Apply {{amount}} cashback",
                      amount: "___PRICE___",
                    }), redemption.redeemableValue)}
              </div>
            </div>
            <Toggle
              on={useRewards}
              onChange={() => setUseRewards((v) => !v)}
              disabled={redemption.disabled || redemption.loading}
            />
          </Card>
        )}

        {/* Cross-sell — single subtle card above the order summary (Block 4). */}
        <CheckoutCrossSell />

        {/* Recommendation strip (FR-019 Phase 1) — hides itself when the
            engine returns nothing or fails. */}
        <RecommendationStrip trigger="checkout" />

        {/* Free items a promotion auto-granted onto the priced quote (D2). Server-owned:
            never added by the customer, never editable, never removable here. */}
        {grantedLines.map((line, idx) => (
          <GrantedLineRow
            key={line.lineId ?? `${line.itemId}-${idx}`}
            name={getDisplayName(line.itemName as { [k: string]: unknown } | undefined, i18n.language || "en") || line.itemId}
            variantName={
              line.selectedVariants && line.selectedVariants.length > 0
                ? line.selectedVariants
                    .map((v) => getDisplayName(v.variantOptionName as { [k: string]: unknown } | undefined, i18n.language || "en"))
                    .filter(Boolean)
                    .join(", ")
                : null
            }
            promoName={t("v2.checkout.free_item_promo", { defaultValue: "promotion" })}
            subtotal={line.pricingSummary?.subtotalAmount ?? 0}
            currency={currency}
          />
        ))}
        {(priceQuote?.unavailableGrants ?? []).length > 0 && (
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              padding: "8px 12px",
              background: "var(--surface-2, #f4f4f5)",
              borderRadius: 10,
            }}
          >
            {t("v2.checkout.grant_unavailable", {
              defaultValue: "That offer can't be added right now because an item is unavailable.",
            })}
          </div>
        )}

        {priceChangedBanner && (
          <div
            role="status"
            style={{
              fontSize: 13,
              color: "#92400e",
              padding: "10px 12px",
              background: "#fef3c7",
              borderRadius: 10,
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 4,
            }}
          >
            <span>{t("v2.checkout.price_changed_from", { defaultValue: "Your total changed from" })}</span>
            <Price amount={priceChangedBanner.from} />
            <span>{t("v2.checkout.price_changed_to", { defaultValue: "to" })}</span>
            <Price amount={priceChangedBanner.to} />
            <span>{t("v2.checkout.price_changed_suffix", { defaultValue: ". Please review and confirm." })}</span>
          </div>
        )}

        <Card padding={14}>
          {!enforceQuote && (
            <>
              <Row label={t("v2.checkout.subtotal")} value={<Price amount={finalPricing.itemsSubtotal} />} />
              {discount > 0 && (
                <Row
                  label={t("v2.cart.promo_discount")}
                  value={<span style={{color: "var(--color-primary-main)"}}>−<Price amount={discount} /></span>}
                  accent
                />
              )}
              {!isDineIn && mode === "delivery" && (
                <Row label={t("v2.cart.delivery")} value={<Price amount={finalPricing.deliveryFeeAmount} />} />
              )}
              {finalServiceTax > 0 && (
                <Row label={t("v2.cart.service_tax")} value={<Price amount={finalServiceTax} />} />
              )}
              {appliedSurcharges
                .filter((s) => !s.waived)
                .map((s) => (
                  <Row
                    key={s.surchargeId}
                    label={getDisplayName(s.name as { [k: string]: unknown }, i18n.language || "en")}
                    value={<Price amount={s.amount} />}
                  />
                ))}
              {tax > 0 && (
                <Row label={t("v2.cart.estimated_tax")} value={<Price amount={tax} />} />
              )}
              {tipsEnabled && requestedTip > 0 && (
                <Row label={t("v2.checkout.tip")} value={<Price amount={requestedTip} />} />
              )}
              {usedReward > 0 && (
                <Row
                  label={t("v2.checkout.rewards", { defaultValue: "Rewards" })}
                  value={<span style={{color: "var(--color-primary-main)"}}>−<Price amount={usedReward} /></span>}
                  accent
                />
              )}
              <div style={{ height: 1, background: "var(--border)", margin: "10px 0" }} />
              <Row label={t("v2.checkout.total")} value={<Price amount={grand} />} bold />
              <StorefrontNote variant="inline" />
            </>
          )}
          {!priceQuote && priceQuoteBusy && (
            <div style={{ fontSize: 13, color: "var(--ink-2)", padding: "6px 0" }}>
              {t("v2.checkout.pricing_loading", { defaultValue: "Calculating your total…" })}
            </div>
          )}
          {priceQuoteFailed && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "6px 0" }}>
              <div style={{ fontSize: 13, color: "#b91c1c" }}>
                {t("v2.checkout.pricing_failed", {
                  defaultValue: "We couldn't price your order. Try again.",
                })}
              </div>
              <Button size="sm" onClick={retryPriceQuote}>
                {t("v2.checkout.retry", { defaultValue: "Try again" })}
              </Button>
            </div>
          )}
          {priceQuote && (
            <>
              <Row label={t("v2.checkout.subtotal")} value={<Price amount={quotedPricing?.itemsSubtotal ?? 0} />} />
              {discount > 0 && (
                <Row
                  label={t("v2.cart.promo_discount")}
                  value={<span style={{color: "var(--color-primary-main)"}}>−<Price amount={discount} /></span>}
                  accent
                />
              )}
              {!isDineIn && mode === "delivery" && (
                <Row label={t("v2.cart.delivery")} value={<Price amount={quoteDeliveryFee} />} />
              )}
              {quoteServiceFee > 0 && (
                <Row label={t("v2.cart.service_tax")} value={<Price amount={quoteServiceFee} />} />
              )}
              {displayedSurcharges
                .filter((s) => !s.waived)
                .map((s) => (
                  <Row
                    key={s.surchargeId}
                    label={getDisplayName(s.name as { [k: string]: unknown }, i18n.language || "en")}
                    value={<Price amount={s.amount} />}
                  />
                ))}
              {tax > 0 && (
                <Row label={t("v2.cart.estimated_tax")} value={<Price amount={tax} />} />
              )}
              {quotedTip > 0 && (
                <Row label={t("v2.checkout.tip")} value={<Price amount={quotedTip} />} />
              )}
              {quotedReward > 0 && (
                <Row
                  label={t("v2.checkout.rewards", { defaultValue: "Rewards" })}
                  value={<span style={{color: "var(--color-primary-main)"}}>−<Price amount={quotedReward} /></span>}
                  accent
                />
              )}
              <div style={{ height: 1, background: "var(--border)", margin: "10px 0" }} />
              <Row label={t("v2.checkout.total")} value={<Price amount={grand} />} bold />
              {/* Merchant's pricing disclaimer sits directly under the total, where a
                  "prices include tax but not service charge" caveat is actionable. */}
              <StorefrontNote variant="inline" />
            </>
          )}
        </Card>

        {/* Ask for order-status push here (during checkout) as well as on the
            tracking screen — self-hides unless web-push is supported and the
            permission is still "default". Enabling now registers the device so
            the token survives the payment redirect. */}
        <PushOptIn />

        {error && !failedPayment && (
          <div
            role="alert"
            style={{
              fontSize: 13,
              color: "#b91c1c",
              padding: "10px 12px",
              background: "#fee2e2",
              borderRadius: 10,
            }}
          >
            <div>{error}</div>
          </div>
        )}
      </div>

      <StickyBar
        style={{
          bottom: 0,
          padding: "12px 16px",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          zIndex: 30,
        }}
      >
        <Button
          full
          size="lg"
          testId="place-order"
          onClick={onPlace}
          disabled={
            placing ||
            !!paymentSheet ||
            !!failedPayment ||
            checkoutBlocker?.kind === "closed" ||
            checkoutBlocker?.kind === "branch_loading" ||
            (orderType === OrderTypeEnum.Delivery && minOrder > 0 && subtotal < minOrder) ||
            (showChangeFor && changeForError(changeForRaw, grand) !== null) ||
            // Server-priced checkout (D2): a fresh, successful quote is required before
            // Place order — no fallback to client pricing. Skipped for the dine-in
            // "append to open order" path, which doesn't price a whole order.
            // Shadow mode (local pricing) never waits on a quote.
            (enforceQuote && !appendOrderId && (!priceQuote || priceQuoteBusy || priceQuoteFailed))
          }
          trailing={<Icons.chevron size={18} />}
        >
          {placing
            ? t("v2.checkout.placing_order")
            : orderType === OrderTypeEnum.Delivery && minOrder > 0 && subtotal < minOrder
            ? injectPrice(
                t("v2.cart.min_order_btn", {
                  amount: "___PRICE___",
                  defaultValue: "Min order ___PRICE___",
                }),
                minOrder
              )
            : placeOrderLabel}
        </Button>
        <div
          style={{ fontSize: 11, color: "var(--ink-3)", textAlign: "center", marginTop: 8 }}
        >
          {t("v2.checkout.terms_notice")}
        </div>
      </StickyBar>

      {paymentSheet && (paymentSheet.family === "paymob" || paymentSheet.family === "stripe") && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
          onClick={onCloseSheet}
          role="dialog"
          aria-modal="true"
          aria-label={t("v2.checkout.payment_label")}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--surface-1)",
              width: "100%",
              maxWidth: 480,
              borderRadius: "16px 16px 0 0",
              padding: 16,
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div className="display" style={{ fontSize: 16, fontWeight: 700 }}>
                {paymentSheet.family === "paymob" ? t("v2.checkout.pay_with_paymob") : t("v2.checkout.pay_with_card")}
              </div>
              <button
                onClick={onCloseSheet}
                aria-label={t("v2.common.close")}
                style={{
                  background: "transparent",
                  border: 0,
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  color: "var(--ink-2)",
                }}
              >
                <Icons.close size={18} />
              </button>
            </div>
            {paymentSheet.family === "paymob" && (
              <div id={PAYMENT_SHEET_ID} style={{ minHeight: 320 }} />
            )}
            {paymentSheet.family === "stripe" && (
              <div>
                <div
                  id={STRIPE_ELEMENT_ID}
                  style={{
                    padding: 14,
                    border: "1px solid var(--border)",
                    borderRadius: 10,
                    background: "var(--surface-1)",
                    marginBottom: 12,
                  }}
                />
                <Button full size="lg" onClick={onConfirmStripe} disabled={confirming}>
                  {confirming ? t("v2.checkout.confirming") : injectPrice(t("v2.checkout.pay_amount", { amount: "___PRICE___" }), grand)}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* <AuthFlow open={signInOpen} onClose={() => setSignInOpen(false)} /> */}
      {failedPayment && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          role="alertdialog"
          aria-modal="true"
          aria-label={t("v2.checkout.payment_label")}
        >
          <div
            style={{
              background: "var(--surface-1)",
              width: "100%",
              maxWidth: 360,
              borderRadius: 16,
              padding: 20,
              boxShadow: "0 24px 48px rgba(0,0,0,0.25)",
              textAlign: "center",
            }}
          >
            <div className="display"
              aria-hidden
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "#fee2e2",
                color: "#b91c1c",
                display: "grid",
                placeItems: "center",
                margin: "0 auto 12px",
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              !
            </div>
            <div className="display" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
              {t("v2.checkout.payment.failed_title")}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 16 }}>
              {error ?? t("v2.checkout.payment.err_failed_retry")}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button full size="lg" onClick={onRetryPayment}>
                {t("v2.checkout.payment.retry")}
              </Button>
              <Button full size="lg" variant="ghost" onClick={onCancelAndRestart}>
                {t("v2.checkout.payment.cancel_and_restart")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
    </div>
  );
}
